'use client';

import { useMemo } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import type { RealtimeVoiceDelegationTarget } from '@/lib/realtime-voice-agent';
import { createRealtimeVoiceThreadDispatch } from '@/lib/realtime-voice-thread-dispatch';

/** Dispatches live voice work to its original chat without a mounted chat page. */
export function useRealtimeVoiceThreadDispatch({
  orgThreadId,
  orgId,
}: {
  orgThreadId: Id<'orgThreads'>;
  orgId: Id<'organizations'>;
}): RealtimeVoiceDelegationTarget {
  const thread = useQuery(api.organizations.threads.queries.getById, {
    id: orgThreadId,
  });
  const send = useMutation(
    api.products.ai.mutations.org_thread_send_message.sendMessage
  );
  const enqueue = useMutation(
    api.organizations.threads.mutations.enqueueMessage
  );
  const stopStreaming = useAction(
    api.products.ai.actions.org_thread_actions.stopStreaming
  );

  return useMemo(
    () =>
      createRealtimeVoiceThreadDispatch({
        orgThreadId,
        orgId,
        getThread: () => thread,
        send,
        enqueue,
        stop: async ({ expectedPromptMessageId, expectedWorkflowId }) => {
          const result = await stopStreaming({
            orgThreadId,
            expectedPromptMessageId,
            expectedWorkflowId,
            requestSource: 'org_chat_composer',
            reportSubAgentCancellation: true,
          });
          return result?.turnStop === 'stopped';
        },
      }),
    [orgThreadId, orgId, thread, send, enqueue, stopStreaming]
  );
}
